import { useState, useEffect, useContext } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Flex from '../../../components/base/Flex';
import OutlinedButton from '../../../components/base/OutlinedButton';
import ContainedButton from '../../../components/base/ContainedButton';
import CustomModal from '../../../components/base/CustomModal';
import { toast } from 'react-toastify';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ProjectContext, { useActiveProject } from '../../../store/projectState';
import { Collapse, IconButton, List, ListItem, ListItemText } from '@mui/material';
import { useScandiumMutation } from '../../../data-layer/utils';
import { useFetchFolders } from '../../../data-layer/project-management';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FolderIcon from '@mui/icons-material/Folder';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';

const FolderRow = ({
  folder,
  selectedFolder,
  onSelect,
  onCreateSubfolder,
  onShowFolderDetails,
  level = 0
}) => {
  const [expanded, setExpanded] = useState(false);
  const isSelected = selectedFolder?.id === folder.id;

  const handleToggle = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  const handleSelect = () => {
    onSelect(folder);
  };

  return (
    <Box sx={{ pl: level * 2 }}>
      <ListItem
        onClick={handleSelect}
        selected={isSelected}
        sx={{
          borderRadius: 1,
          backgroundColor: isSelected ? 'primary.lighter' : 'transparent',
          border: isSelected ? '1px solid primary.main' : '1px solid transparent',
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }}>
        {folder.subdirectories?.length > 0 ? (
          <IconButton size="small" onClick={handleToggle} sx={{ mr: 1 }}>
            {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
        ) : (
          <Box sx={{ width: 32 }} />
        )}
        <FolderIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
        <ListItemText primary={folder.name} primaryTypographyProps={{ variant: 'body2' }} />
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onCreateSubfolder(folder.id);
            }}
            title="Add subfolder">
            <AddIcon fontSize="small" color="primary" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onShowFolderDetails(folder);
            }}
            title="Folder details">
            <InfoIcon fontSize="small" color="primary" />
          </IconButton>
        </Box>
      </ListItem>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {folder.subdirectories?.map((subfolder) => (
            <FolderRow
              key={subfolder.id}
              folder={subfolder}
              selectedFolder={selectedFolder}
              onSelect={onSelect}
              onCreateSubfolder={onCreateSubfolder}
              onShowFolderDetails={onShowFolderDetails}
              level={level + 1}
            />
          ))}
        </List>
      </Collapse>
    </Box>
  );
};

const CopyTestCasesModal = ({ open, onClose, selectedTestIds }) => {
  const { projects, isProjectsLoading } = useContext(ProjectContext);
  const activeProject = useActiveProject();
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [expandedProject, setExpandedProject] = useState(null);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [showFolderDetails, setShowFolderDetails] = useState(false);
  const [selectedFolderDetails, setSelectedFolderDetails] = useState(null);
  const [newFolderParentId, setNewFolderParentId] = useState(null);
  const [folderForm, setFolderForm] = useState({
    name: '',
    description: ''
  });

  const availableProjects = projects?.filter((project) => project.type !== 'mobile') || [];

  const {
    data: folders,
    isLoading: isFoldersLoading,
    refetch: fetchFolders
  } = useFetchFolders(expandedProject?.id, {
    enabled: !!expandedProject?.id
  });

  const { mutateAsync: duplicateTests, isLoading: isCopying } = useScandiumMutation(
    `/projects/${activeProject?.id}/test-cases/duplicate`,
    {
      onSuccess: (data) => {
        toast.success(data.message);
      },
      onError: (error) => {
        toast.error(error.message);
      }
    }
  );

  const { mutateAsync: createFolder, isLoading: isCreatingFolder } = useScandiumMutation(
    `/projects/${expandedProject?.id}/folders`,
    {
      method: 'POST',
      onSuccess: (data) => {
        toast.success(data.message);
        setShowCreateFolder(false);
        setFolderForm({ name: '', description: '' });
        fetchFolders();
      },
      onError: (error) => {
        toast.error(error.message);
      }
    }
  );

  const handleProjectClick = async (project) => {
    if (expandedProject?.id === project.id) {
      setExpandedProject(null);
      setSelectedFolder(null);
    } else {
      setExpandedProject(project);
      setSelectedProject(project);
      setSelectedFolder(null);
      await fetchFolders();
    }
  };

  const handleFolderSelect = (folder) => {
    setSelectedFolder(folder);
  };

  const handleCreateNewFolder = () => {
    setNewFolderParentId(null);
    setShowCreateFolder(true);
  };

  const handleCreateSubfolder = (parentId) => {
    setNewFolderParentId(parentId);
    setShowCreateFolder(true);
  };

  const handleShowFolderDetails = (folder) => {
    setSelectedFolderDetails(folder);
    setShowFolderDetails(true);
  };

  const handleFolderFormSubmit = async (e) => {
    e.preventDefault();
    if (!folderForm.name.trim()) {
      toast.error('Folder name is required');
      return;
    }

    const payload = {
      ...folderForm,
      ...(newFolderParentId ? { parent_id: newFolderParentId } : {})
    };

    await createFolder(payload);
  };

  const handleCopyTestCases = async () => {
    if (!selectedProject || !selectedTestIds.length) {
      toast.error('Please select a project and at least one test case');
      return;
    }

    const payload = {
      test_cases: selectedTestIds,
      new_project_id: selectedProject.id,
      ...(selectedFolder && { folder_id: selectedFolder.id })
    };

    try {
      await duplicateTests(payload);
      toast.success(`${selectedTestIds.length} test cases copied to ${selectedProject.name}`);
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to copy test cases');
    }
  };

  useEffect(() => {
    if (!open) {
      setExpandedProject(null);
      setSelectedProject(null);
      setSelectedFolder(null);
      setShowCreateFolder(false);
      setShowFolderDetails(false);
      setFolderForm({ name: '', description: '' });
    }
  }, [open]);

  return (
    <>
      <CustomModal open={open} onClose={onClose}>
        <Box sx={{ width: '34rem', maxWidth: '100%' }}>
          <Box
            sx={{
              bgcolor: 'background.paper',
              px: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
            <Flex alignItems="center" gap={2}>
              <ContentCopyIcon color="primary" fontSize="medium" />
              <Typography
                variant="h6"
                color={'primary'}
                sx={{
                  fontSize: '1.5rem',
                  fontWeight: 600
                }}>
                Copy {selectedTestIds?.length} Test Cases
              </Typography>
            </Flex>
            <IconButton onClick={onClose} sx={{ color: 'primary' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ px: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Select the desired folder to copy the test cases to
            </Typography>

            <Flex alignItems="center" gap={1} sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Save to:</Typography>
              <Typography color="primary">
                {selectedProject
                  ? `${selectedProject.name}${selectedFolder ? `/${selectedFolder.name}` : ''}`
                  : 'Select a project'}
              </Typography>
            </Flex>

            <Box
              sx={{
                height: '20rem',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'auto',
                p: 1
              }}>
              {isProjectsLoading ? (
                <Flex justifyContent="center" p={3}>
                  <CircularProgress size={24} color="secondary" />
                </Flex>
              ) : (
                <List>
                  {availableProjects.map((project) => (
                    <Box key={project.id}>
                      <ListItem
                        button
                        onClick={() => handleProjectClick(project)}
                        selected={selectedProject?.id === project.id}
                        sx={{
                          borderRadius: 1,
                          backgroundColor:
                            selectedProject?.id === project.id ? 'primary.lighter' : 'transparent',
                          border:
                            selectedProject?.id === project.id
                              ? '1px solid primary.main'
                              : '1px solid transparent',
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          }
                        }}>
                        {expandedProject?.id === project.id ? (
                          <ExpandMoreIcon color="primary" sx={{ mr: 1 }} />
                        ) : (
                          <ChevronRightIcon color="primary" sx={{ mr: 1 }} />
                        )}
                        <ListItemText
                          primary={project.name}
                          secondary={`${project.type} • ${project.test_cases_count} test cases`}
                        />
                        {activeProject?.id === project.id && (
                          <Box
                            component="span"
                            sx={{
                              px: 1,
                              py: 0.5,
                              borderRadius: 4,
                              bgcolor: 'success.light',
                              color: 'success.dark',
                              fontSize: '0.75rem',
                              fontWeight: 500
                            }}>
                            Active project
                          </Box>
                        )}
                        {expandedProject?.id === project.id && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCreateNewFolder();
                            }}
                            sx={{ ml: 'auto' }}
                            title="Add folder">
                            <AddIcon color="primary" />
                          </IconButton>
                        )}
                      </ListItem>

                      <Collapse
                        in={expandedProject?.id === project.id}
                        timeout="auto"
                        unmountOnExit>
                        {isFoldersLoading ? (
                          <Flex justifyContent="center" p={2}>
                            <CircularProgress size={20} />
                          </Flex>
                        ) : folders?.data?.length ? (
                          <Box sx={{ pl: 2 }}>
                            {folders.data.map((folder) => (
                              <FolderRow
                                key={folder.id}
                                folder={folder}
                                selectedFolder={selectedFolder}
                                onSelect={handleFolderSelect}
                                onCreateSubfolder={handleCreateSubfolder}
                                onShowFolderDetails={handleShowFolderDetails}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textAlign: 'center', py: 2 }}>
                            No folders found
                          </Typography>
                        )}
                      </Collapse>
                    </Box>
                  ))}
                </List>
              )}
            </Box>

            <Flex justifyContent="flex-end" gap={2} sx={{ pt: 3 }}>
              <OutlinedButton onClick={onClose}>Cancel</OutlinedButton>
              <ContainedButton
                onClick={handleCopyTestCases}
                color="primary"
                disabled={!selectedProject || isCopying}
                startIcon={isCopying ? <CircularProgress size={20} color="inherit" /> : null}>
                {isCopying ? 'Copying...' : 'Copy'}
              </ContainedButton>
            </Flex>
          </Box>
        </Box>
      </CustomModal>

      {/* Create Folder Modal */}
      <CustomModal open={showCreateFolder} onClose={() => setShowCreateFolder(false)}>
        <Box sx={{ width: '30rem', maxWidth: '100%', p: 3 }}>
          <Flex alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight={600}>
              {newFolderParentId ? 'Create Subfolder' : 'Create New Folder'}
            </Typography>
            <IconButton onClick={() => setShowCreateFolder(false)}>
              <CloseIcon />
            </IconButton>
          </Flex>

          <Box component="form" onSubmit={handleFolderFormSubmit} sx={{ '& > * + *': { mt: 2 } }}>
            <Box>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                Folder Name
              </Typography>
              <Box
                component="input"
                fullWidth
                value={folderForm.name}
                onChange={(e) => setFolderForm({ ...folderForm, name: e.target.value })}
                placeholder="Enter folder name"
                required
                sx={{
                  px: 2,
                  py: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  width: '100%',
                  '&:focus': {
                    outline: 'none',
                    borderColor: 'primary.main',
                    boxShadow: '0 0 0 2px rgba(0, 82, 204, 0.2)'
                  }
                }}
              />
            </Box>

            <Box>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                Description (Optional)
              </Typography>
              <Box
                component="textarea"
                fullWidth
                rows={3}
                value={folderForm.description}
                onChange={(e) => setFolderForm({ ...folderForm, description: e.target.value })}
                placeholder="Enter folder description"
                sx={{
                  px: 2,
                  py: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  width: '100%',
                  resize: 'none',
                  '&:focus': {
                    outline: 'none',
                    borderColor: 'primary.main',
                    boxShadow: '0 0 0 2px rgba(0, 82, 204, 0.2)'
                  }
                }}
              />
            </Box>

            <Flex justifyContent="flex-end" gap={2} sx={{ pt: 2 }}>
              <OutlinedButton onClick={() => setShowCreateFolder(false)}>Cancel</OutlinedButton>
              <ContainedButton
                type="submit"
                color="primary"
                disabled={isCreatingFolder}
                startIcon={
                  isCreatingFolder ? <CircularProgress size={20} color="inherit" /> : null
                }>
                {isCreatingFolder ? 'Creating...' : 'Create Folder'}
              </ContainedButton>
            </Flex>
          </Box>
        </Box>
      </CustomModal>

      {/* Folder Details Modal */}
      <CustomModal open={showFolderDetails} onClose={() => setShowFolderDetails(false)}>
        <Box sx={{ width: '30rem', maxWidth: '100%', p: 3 }}>
          <Flex alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight={600}>
              Folder Details
            </Typography>
            <IconButton onClick={() => setShowFolderDetails(false)}>
              <CloseIcon />
            </IconButton>
          </Flex>

          {selectedFolderDetails && (
            <Box sx={{ '& > * + *': { mt: 2 } }}>
              <Box>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Folder Name
                </Typography>
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: 'action.hover',
                    borderRadius: 1
                  }}>
                  {selectedFolderDetails.name}
                </Box>
              </Box>

              <Box>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Description
                </Typography>
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    minHeight: '5rem'
                  }}>
                  {selectedFolderDetails.description || 'No description provided'}
                </Box>
              </Box>

              <Flex justifyContent="flex-end" sx={{ pt: 2 }}>
                <OutlinedButton onClick={() => setShowFolderDetails(false)}>Close</OutlinedButton>
              </Flex>
            </Box>
          )}
        </Box>
      </CustomModal>
    </>
  );
};

export default CopyTestCasesModal;
